<template>
    <div class="hidden md:flex-1 md:flex md:items-center md:justify-between">
        <PopoverGroup as="nav" class="flex space-x-8 lg:space-x-10">
            <NavBarLeftProduct
                v-if="product"
                :product="product"
                :productPages="productPages"
            />

            <Popover v-if="showProductsMenu" v-slot="{ open }">
                <PopoverButton
                    data-test="products"
                    :class="[
                        open
                            ? 'text-nutreance-brown-darkest'
                            : 'text-nutreance-brown',
                        'group bg-navbar-brown rounded-md inline-flex items-center text-base font-medium hover:text-nutreance-brown-darkest focus:outline-none',
                    ]"
                >
                    <span>Products</span>
                    <ChevronDownIcon
                        :class="[
                            open
                                ? 'text-nutreance-brown-darkest'
                                : 'text-nutreance-brown',
                            'ml-2 h-5 w-5 group-hover:text-nutreance-brown-darkest',
                        ]"
                        aria-hidden="true"
                    />
                </PopoverButton>

                <transition
                    enter-active-class="transition ease-out duration-200"
                    enter-from-class="opacity-0 -translate-y-1"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition ease-in duration-150"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-1"
                >
                    <PopoverPanel
                        class="hidden md:block absolute z-20 top-full inset-x-0 transform shadow-lg bg-white border-b border-gray-200"
                    >
                        <div
                            class="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8"
                        >
                            <div v-if="loading" class="py-12 text-center">
                                <RefreshIcon
                                    class="mx-auto h-24 w-24 animate-spin text-gray-400"
                                />
                            </div>

                            <div
                                v-else-if="!productCategory"
                                class="grid gap-y-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-4"
                            >
                                <button
                                    role="button"
                                    v-for="category in productCategories"
                                    :key="category.id"
                                    @click="productCategory = category"
                                    class="-m-3 p-3 flex flex-col justify-between rounded-lg hover:bg-gray-50 focus:outline-none"
                                >
                                    <span
                                        class="flex items-center w-full md:h-full lg:flex-col"
                                    >
                                        <span class="shrink-0">
                                            <span
                                                class="inline-flex items-center justify-center h-10 w-10 rounded-md bg-nutreance-green text-nutreance-green sm:h-16 sm:w-16"
                                            >
                                                <svg-vue
                                                    class="h-8 w-8 sm:h-12 sm:w-12"
                                                    :icon="category.svg"
                                                    :key="category.svg"
                                                    aria-hidden="true"
                                                />
                                            </span>
                                        </span>

                                        <span
                                            class="ml-4 md:flex-1 md:flex md:flex-col md:justify-between lg:ml-0 lg:mt-4"
                                        >
                                            <span>
                                                <p
                                                    class="text-base font-medium text-gray-900"
                                                >
                                                    {{ category.name }}
                                                </p>
                                            </span>
                                        </span>
                                    </span>
                                </button>
                            </div>

                            <div
                                v-else
                                class="grid gap-y-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-4"
                            >
                                <button
                                    role="button"
                                    @click="productCategory = null"
                                    class="-m-3 p-3 flex flex-col justify-center rounded-lg hover:bg-gray-50 focus:outline-none"
                                >
                                    <span
                                        class="flex items-center w-full lg:flex-col"
                                    >
                                        <span class="shrink-0">
                                            <span
                                                class="inline-flex items-center justify-center h-10 w-10 rounded-md bg-nutreance-green text-white sm:h-16 sm:w-16"
                                            >
                                                <ReplyIcon
                                                    class="h-6 w-6 sm:h-10 sm:w-10"
                                                    aria-hidden="true"
                                                />
                                            </span>
                                        </span>

                                        <span
                                            class="ml-4 md:flex-1 md:flex md:flex-col md:justify-between lg:ml-0 lg:mt-4"
                                        >
                                            <span>
                                                <p
                                                    class="text-base font-medium text-gray-900"
                                                >
                                                    Back
                                                </p>
                                            </span>
                                        </span>
                                    </span>
                                </button>

                                <a
                                    v-for="item in productCategory.products"
                                    :key="item.id"
                                    :href="`/products/${item.slug}`"
                                    class="-m-3 p-3 flex flex-col justify-between rounded-lg hover:bg-gray-50"
                                >
                                    <span
                                        class="flex items-center w-full md:h-full lg:flex-col"
                                    >
                                        <span class="shrink-0">
                                            <span
                                                class="inline-flex items-center justify-center h-10 w-10 sm:h-16 sm:w-16"
                                            >
                                                <ProductIcon
                                                    :imgClass="'h-10 sm:h-16'"
                                                    :product="item"
                                                />
                                            </span>
                                        </span>

                                        <span
                                            class="ml-4 md:flex-1 md:flex md:flex-col md:justify-between lg:ml-0"
                                        >
                                            <span>
                                                <p
                                                    class="text-center text-base font-semibold text-gray-900"
                                                >
                                                    {{ item.name }}
                                                </p>
                                                <p
                                                    class="text-center text-sm font-medium text-gray-700"
                                                >
                                                    {{ item.description }}
                                                </p>
                                            </span>
                                        </span>
                                    </span>
                                </a>
                            </div>
                        </div>

                        <div class="bg-gray-50 border-t border-gray-200">
                            <div
                                class="max-w-7xl mx-auto space-y-6 px-4 py-5 sm:justify-center sm:flex sm:space-y-0 sm:space-x-10 sm:px-6 lg:px-8"
                            >
                                <div
                                    v-for="item in actions"
                                    :key="item.name"
                                    class="flow-root"
                                >
                                    <a
                                        :href="item.href"
                                        class="-m-3 p-3 flex items-center rounded-md text-base font-medium text-gray-900 hover:bg-gray-100"
                                    >
                                        <component
                                            :is="item.icon"
                                            class="shrink-0 h-7 w-7 text-gray-400"
                                            aria-hidden="true"
                                        />
                                        <span class="hidden lg:inline ml-3">{{
                                            item.longName
                                        }}</span>
                                        <span class="lg:hidden ml-3">{{
                                            item.shortName
                                        }}</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </PopoverPanel>
                </transition>
            </Popover>

            <Popover class="relative" v-slot="{ open }">
                <PopoverButton
                    data-test="help"
                    :class="[
                        open
                            ? 'text-nutreance-brown-darkest'
                            : 'text-nutreance-brown',
                        'group bg-navbar-brown rounded-md inline-flex items-center text-base font-medium hover:text-nutreance-brown-darkest focus:outline-none',
                    ]"
                >
                    <span>{{
                        isCustomer || isGuest ? "Help" : "Company Info"
                    }}</span>
                    <ChevronDownIcon
                        :class="[
                            open
                                ? 'text-nutreance-brown-darkest'
                                : 'text-nutreance-brown',
                            'ml-2 h-5 w-5 group-hover:text-nutreance-brown-darkest',
                        ]"
                        aria-hidden="true"
                    />
                </PopoverButton>

                <transition
                    enter-active-class="transition ease-out duration-200"
                    enter-from-class="opacity-0 translate-y-1"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition ease-in duration-150"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 translate-y-1"
                >
                    <PopoverPanel
                        class="absolute z-20 -left-24 mt-3 transform w-screen max-w-sm"
                    >
                        <div
                            class="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden"
                        >
                            <div
                                class="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8"
                            >
                                <a
                                    :href="phoneLink"
                                    class="-m-3 p-3 flex items-center rounded-lg hover:bg-gray-50"
                                >
                                    <div
                                        class="shrink-0 flex items-center justify-center h-10 w-10 rounded-md bg-nutreance-green text-white sm:h-12 sm:w-12"
                                    >
                                        <PhoneIcon
                                            class="h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    </div>

                                    <div class="ml-4">
                                        <p
                                            class="text-base font-medium text-gray-900"
                                        >
                                            {{ phone }}
                                        </p>
                                    </div>
                                </a>
                                <a
                                    :href="headquarters.emailLink"
                                    class="-m-3 p-3 flex items-center rounded-lg hover:bg-gray-50"
                                >
                                    <div
                                        class="shrink-0 flex items-center justify-center h-10 w-10 rounded-md bg-nutreance-green text-white sm:h-12 sm:w-12"
                                    >
                                        <MailIcon
                                            class="h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    </div>

                                    <div class="ml-4">
                                        <p
                                            class="text-base font-medium text-gray-900"
                                        >
                                            {{ headquarters.email }}
                                        </p>
                                    </div>
                                </a>
                                <div
                                    v-if="!isGuest && !isCustomer"
                                    class="-m-3 p-3 flex items-start rounded-lg hover:bg-gray-50"
                                >
                                    <div
                                        class="shrink-0 flex items-center justify-center h-10 w-10 rounded-md bg-nutreance-green text-white sm:h-12 sm:w-12"
                                    >
                                        <LocationMarkerIcon
                                            class="h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div class="ml-4">
                                        <p
                                            class="text-base font-medium text-gray-900"
                                        >
                                            {{ headquarters.name }}
                                        </p>
                                        <p class="text-base text-gray-900">
                                            {{ headquarters.line1 }}
                                        </p>
                                        <p class="text-base text-gray-900">
                                            {{ headquarters.city }},
                                            {{ headquarters.state }}
                                            {{ headquarters.postal_code }}
                                        </p>
                                        <p class="text-base text-gray-900">
                                            United States
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="px-5 bg-gray-50 sm:px-8 sm:py-4">
                                <a
                                    v-for="item in help"
                                    :key="item.name"
                                    :href="item.href"
                                    class="-mx-3 p-3 flow-root rounded-md hover:bg-gray-100"
                                >
                                    <div class="flex items-center">
                                        <component
                                            :is="item.icon"
                                            class="shrink-0 h-6 w-6 text-gray-400"
                                            aria-hidden="true"
                                        />
                                        <span
                                            class="text-base font-medium text-gray-900 ml-3"
                                            >{{ item.name }}</span
                                        >
                                    </div>
                                </a>
                            </div>
                        </div>
                    </PopoverPanel>
                </transition>
            </Popover>
        </PopoverGroup>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import {
    Popover,
    PopoverButton,
    PopoverGroup,
    PopoverPanel,
} from "@headlessui/vue";
import {
    BeakerIcon,
    ChatIcon,
    InformationCircleIcon,
    LocationMarkerIcon,
    MailIcon,
    PhoneIcon,
    QuestionMarkCircleIcon,
    RefreshIcon,
    ReplyIcon,
    TicketIcon,
} from "@heroicons/vue/outline";
import { ChevronDownIcon } from "@heroicons/vue/solid";
import NavBarLeftProduct from "./NavBarLeftProduct.vue";
import ProductIcon from "../products/ProductIcon.vue";

const help = [
    {
        name: "Return Policy",
        href: "/return-policy",
        icon: ReplyIcon,
    },
    {
        name: "Contact Us",
        href: "/contact-us",
        icon: ChatIcon,
    },
    {
        name: "About Nutreance",
        href: "/about",
        icon: InformationCircleIcon,
    },
];

const props = defineProps<{
    headquarters?: any;
    isCustomer?: string;
    isGuest?: string;
    loading?: boolean;
    phone?: string;
    phoneLink?: string;
    product?: any;
    productCategories?: any;
    productPages?: any;
    showProductsMenu?: string;
}>();

// FIXME(4f): the `actions` items have no `name`, but the template binds
// `:key="item.name"` (always `undefined` at runtime — a non-unique key bug
// carried over from the Options API). Typed `name?` here to keep behavior
// identical while satisfying vue-tsc.
const actions: Array<{
    name?: string;
    longName?: string;
    shortName: string;
    href?: string;
    icon: any;
}> = [
    {
        longName: props.phone,
        shortName: "Call Us",
        href: props.phoneLink,
        icon: PhoneIcon,
    },
    {
        longName: "Earn Coupons",
        shortName: "Coupons",
        href: "/coupons/send",
        icon: TicketIcon,
    },
    {
        longName: "FAQ",
        shortName: "FAQ",
        href: "/questions",
        icon: QuestionMarkCircleIcon,
    },
    {
        longName: "Quality Control",
        shortName: "Quality",
        href: "/quality-control",
        icon: BeakerIcon,
    },
];
const productCategory = ref<any>(null);
</script>
